class PremiumCartSection extends HTMLElement {
  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDiscountInput = this.handleDiscountInput.bind(this);
    this.handleDiscountKeydown = this.handleDiscountKeydown.bind(this);
    this.handleNoteInput = this.handleNoteInput.bind(this);
    this.handleNoteBlur = this.handleNoteBlur.bind(this);
    this.handleExternalDiscountSync = this.handleExternalDiscountSync.bind(this);

    this.noteTimer = null;
    this.noteSavePromise = Promise.resolve();
    this.isBusy = false;
    this.discountStorageKey = 'customHeaderCartDiscountCode';
    this.discountInputStorageKey = 'customHeaderCartDiscountInputValue';
    this.discountSyncEventName = 'premium-cart:discount-sync';
    this.activeDiscountSuccessBurstFrame = 0;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.sectionId = this.dataset.sectionId;
    this.runtime = window.__premiumCartRuntime ||= {
      noteOpen: null,
      discountOpen: null,
      lastKnownCartState: null,
      pendingDiscountSuccessEffect: false,
    };

    this.refreshReferences();
    this.initializeAuxiliaryState();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    window.addEventListener(this.discountSyncEventName, this.handleExternalDiscountSync);

    if (this.discountInput) {
      this.discountInput.addEventListener('input', this.handleDiscountInput);
      this.discountInput.addEventListener('keydown', this.handleDiscountKeydown);
    }

    if (this.noteInput) {
      this.noteInput.addEventListener('input', this.handleNoteInput);
      this.noteInput.addEventListener('blur', this.handleNoteBlur);
    }

    if (this.runtime.pendingDiscountSuccessEffect) {
      this.runtime.pendingDiscountSuccessEffect = false;
      window.requestAnimationFrame(() => {
        this.triggerDiscountSuccessEffect();
      });
    }

    if (this.runtime.lastKnownCartState) {
      this.syncStoredDiscountCodeWithCartState(this.runtime.lastKnownCartState);
      this.updateDiscountActionState(this.getStoredDiscountCode(), {
        inputValue: (this.discountInput && this.discountInput.value) || this.getStoredDiscountInputValue(),
      });
      return;
    }

    this.fetchCartState()
      .then((cartState) => {
        this.syncStoredDiscountCodeWithCartState(cartState);
        this.updateDiscountActionState(this.getStoredDiscountCode(), {
          inputValue: (this.discountInput && this.discountInput.value) || this.getStoredDiscountInputValue(),
        });
      })
      .catch(() => {
        this.updateDiscountActionState(this.getStoredDiscountCode(), {
          inputValue: (this.discountInput && this.discountInput.value) || this.getStoredDiscountInputValue(),
        });
      });
  }

  disconnectedCallback() {
    window.clearTimeout(this.noteTimer);

    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    window.removeEventListener(this.discountSyncEventName, this.handleExternalDiscountSync);

    if (this.discountInput) {
      this.discountInput.removeEventListener('input', this.handleDiscountInput);
      this.discountInput.removeEventListener('keydown', this.handleDiscountKeydown);
    }

    if (this.noteInput) {
      this.noteInput.removeEventListener('input', this.handleNoteInput);
      this.noteInput.removeEventListener('blur', this.handleNoteBlur);
    }
  }

  refreshReferences() {
    this.statusNode = this.querySelector('[data-cart-status]');
    this.noteSection = this.querySelector('[data-cart-note-section]');
    this.noteToggle = this.querySelector('[data-cart-note-toggle]');
    this.noteInput = this.querySelector('[data-cart-note-input]');
    this.discountSection = this.querySelector('[data-cart-discount-section]');
    this.discountToggle = this.querySelector('[data-cart-discount-toggle]');
    this.discountInput = this.querySelector('[data-cart-discount-input]');
    this.discountApply = this.querySelector('[data-cart-discount-apply]');
    this.discountApplyLabel = this.querySelector('[data-cart-discount-apply-label]');
    this.appliedDiscountCodeDisplays = Array.from(this.querySelectorAll('[data-cart-applied-discount-code]'));
    this.successBurst = this.querySelector('[data-premium-cart-success-burst]');
    this.successBurstCanvas = this.querySelector('[data-premium-cart-success-burst-canvas]');
    this.discountApplyText = this.discountApply ? this.discountApply.dataset.applyLabel || 'Apply discount' : 'Apply discount';
    this.discountRemoveText = this.discountApply ? this.discountApply.dataset.removeLabel || 'Remove code' : 'Remove code';
    this.savedNoteValue = this.noteInput ? String(this.noteInput.value || '') : '';
  }

  initializeAuxiliaryState() {
    const storedDiscount = this.getStoredDiscountCode();
    const storedDiscountInput = this.getStoredDiscountInputValue();
    const noteDefault = this.noteSection && this.noteSection.dataset.noteOpenDefault === 'true';

    if (this.noteInput) {
      this.savedNoteValue = String(this.noteInput.value || '');
    }

    if (this.discountInput) {
      this.discountInput.value = storedDiscount || storedDiscountInput;
      if (storedDiscount) {
        this.setStoredDiscountInputValue(storedDiscount);
      }
    }

    this.setDiscountInputErrorState(false);
    this.setNoteOpen(this.runtime.noteOpen !== null ? this.runtime.noteOpen : Boolean(noteDefault));
    this.setDiscountOpen(this.runtime.discountOpen !== null ? this.runtime.discountOpen : Boolean(storedDiscount));
    this.updateDiscountActionState(storedDiscount, {
      inputValue: (this.discountInput && this.discountInput.value) || storedDiscountInput,
    });
  }

  handleClick(event) {
    const noteToggle = event.target.closest('[data-cart-note-toggle]');
    if (noteToggle) {
      event.preventDefault();
      this.setNoteOpen(!this.runtime.noteOpen);
      return;
    }

    const discountToggle = event.target.closest('[data-cart-discount-toggle]');
    if (discountToggle) {
      event.preventDefault();
      this.setDiscountOpen(!this.runtime.discountOpen);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-action]');
    if (quantityButton) {
      event.preventDefault();
      this.handleQuantityButton(quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-remove-line]');
    if (removeButton) {
      event.preventDefault();
      const line = Number.parseInt(removeButton.dataset.removeLine, 10);
      if (Number.isInteger(line)) {
        this.updateLine(line, 0, 'Item removed.');
      }
      return;
    }

    const discountButton = event.target.closest('[data-cart-discount-apply]');
    if (discountButton) {
      event.preventDefault();
      this.handleDiscountAction();
      return;
    }

    const checkoutButton = event.target.closest('[data-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      this.proceedToCheckout();
    }
  }

  handleChange(event) {
    const quantityInput = event.target.closest('[data-quantity-input]');
    if (!quantityInput) return;

    const line = Number.parseInt(quantityInput.dataset.line, 10);
    if (!Number.isInteger(line)) return;

    const quantity = this.normalizeQuantity(quantityInput, quantityInput.value);
    quantityInput.value = this.formatQuantityValue(quantityInput, quantity);
    this.updateLine(line, quantity, 'Cart updated.');
  }

  handleDiscountInput() {
    if (!this.discountInput) return;

    const activeStoredDiscount = this.getStoredDiscountCode();
    const nextInputValue = this.normalizeDiscountCodeValue(this.discountInput.value);

    this.setDiscountInputErrorState(false);

    if (activeStoredDiscount) {
      if (nextInputValue !== activeStoredDiscount) {
        this.discountInput.value = activeStoredDiscount;
      }
      this.setStoredDiscountInputValue(activeStoredDiscount);
      this.updateDiscountActionState(activeStoredDiscount, {
        syncInput: false,
        inputValue: activeStoredDiscount,
      });
      this.dispatchDiscountSyncEvent({
        source: 'premium-cart',
        code: activeStoredDiscount,
        inputValue: activeStoredDiscount,
        open: this.runtime.discountOpen,
      });
      return;
    }

    this.setStoredDiscountInputValue(nextInputValue);
    this.updateDiscountActionState('', {
      syncInput: false,
      inputValue: nextInputValue,
    });
    this.dispatchDiscountSyncEvent({
      source: 'premium-cart',
      code: '',
      inputValue: nextInputValue,
      open: this.runtime.discountOpen,
    });
  }

  handleDiscountKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.handleDiscountAction();
  }

  handleNoteInput(event) {
    window.clearTimeout(this.noteTimer);
    this.noteTimer = window.setTimeout(() => {
      this.noteTimer = null;
      this.saveNote(event.target.value);
    }, 250);
  }

  handleNoteBlur() {
    this.flushNote();
  }

  handleQuantityButton(button) {
    const line = Number.parseInt(button.dataset.line, 10);
    const input = this.querySelector(`[data-quantity-input][data-line="${line}"]`);

    if (!Number.isInteger(line) || !input) return;

    const currentQuantity = this.normalizeQuantity(input, input.value);
    const step = Number.parseInt(input.step, 10) || 1;
    const action = button.dataset.quantityAction;
    const nextQuantity = action === 'decrease' ? currentQuantity - step : currentQuantity + step;
    const normalizedQuantity = this.normalizeQuantity(input, nextQuantity);

    input.value = this.formatQuantityValue(input, normalizedQuantity);
    this.updateLine(line, normalizedQuantity, 'Cart updated.');
  }

  normalizeQuantity(input, rawValue) {
    let quantity = Number.parseInt(this.normalizeQuantityDigits(rawValue), 10);
    const max = input.max ? Number.parseInt(input.max, 10) : null;
    const step = Number.parseInt(input.step, 10) || 1;

    if (Number.isNaN(quantity)) quantity = 0;
    if (quantity < 0) quantity = 0;

    if (quantity > 0 && step > 1) {
      quantity = Math.round(quantity / step) * step;
      if (quantity < step) quantity = step;
    }

    if (typeof max === 'number' && !Number.isNaN(max) && quantity > max) {
      quantity = max;
    }

    return quantity;
  }

  normalizeQuantityDigits(rawValue) {
    return String(rawValue ?? '')
      .trim()
      .replace(/[\u0660-\u0669]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
      .replace(/[\u06F0-\u06F9]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
      .replace(/[^\d-]/g, '');
  }

  formatQuantityValue(input, value) {
    const numericValue = String(value ?? '');
    if (input?.dataset?.numeralSystem !== 'arabic') return numericValue;

    return numericValue.replace(/\d/g, (digit) => String.fromCharCode(0x0660 + Number.parseInt(digit, 10)));
  }

  normalizeDiscountCodeValue(value) {
    return String(value || '').trim();
  }

  normalizeDiscountCodeKey(value) {
    return this.normalizeDiscountCodeValue(value).toLowerCase().replace(/[\s_-]+/g, '');
  }

  getStoredDiscountCode() {
    try {
      return window.localStorage.getItem(this.discountStorageKey) || '';
    } catch (error) {
      return '';
    }
  }

  setStoredDiscountCode(value) {
    try {
      if (value) {
        window.localStorage.setItem(this.discountStorageKey, value);
      } else {
        window.localStorage.removeItem(this.discountStorageKey);
      }
    } catch (error) {}
  }

  getStoredDiscountInputValue() {
    try {
      return window.localStorage.getItem(this.discountInputStorageKey) || '';
    } catch (error) {
      return '';
    }
  }

  setStoredDiscountInputValue(value) {
    const normalizedValue = this.normalizeDiscountCodeValue(value);

    try {
      if (normalizedValue) {
        window.localStorage.setItem(this.discountInputStorageKey, normalizedValue);
      } else {
        window.localStorage.removeItem(this.discountInputStorageKey);
      }
    } catch (error) {}
  }

  setNoteOpen(isOpen) {
    this.runtime.noteOpen = Boolean(isOpen);
    if (!this.noteSection || !this.noteToggle) return;

    this.noteSection.classList.toggle('is-open', this.runtime.noteOpen);
    this.noteToggle.setAttribute('aria-expanded', this.runtime.noteOpen ? 'true' : 'false');
  }

  setDiscountOpen(isOpen) {
    this.runtime.discountOpen = Boolean(isOpen);
    if (!this.discountSection || !this.discountToggle) return;

    this.discountSection.classList.toggle('is-open', this.runtime.discountOpen);
    this.discountToggle.setAttribute('aria-expanded', this.runtime.discountOpen ? 'true' : 'false');
  }

  setDiscountBusyState(isBusy) {
    if (!this.discountApply) return;

    this.discountApply.disabled = Boolean(isBusy);
    if (isBusy) {
      this.discountApply.setAttribute('aria-busy', 'true');
    } else {
      this.discountApply.removeAttribute('aria-busy');
    }
  }

  setDiscountInputErrorState(isError) {
    if (!this.discountInput) return;

    this.discountInput.classList.toggle('is-error', Boolean(isError));
    this.discountInput.setAttribute('aria-invalid', isError ? 'true' : 'false');
  }

  dispatchDiscountSyncEvent(detail = {}) {
    try {
      window.dispatchEvent(
        new CustomEvent(this.discountSyncEventName, {
          detail,
        })
      );
    } catch (error) {}
  }

  async handleExternalDiscountSync(event) {
    const detail = event && event.detail ? event.detail : {};
    if (detail.source === 'premium-cart') return;

    const nextCode = this.normalizeDiscountCodeValue(
      detail.code !== undefined ? detail.code : this.getStoredDiscountCode()
    );
    const nextInputValue = this.normalizeDiscountCodeValue(
      detail.inputValue !== undefined ? detail.inputValue : this.getStoredDiscountInputValue()
    );

    this.setStoredDiscountCode(nextCode);
    this.setStoredDiscountInputValue(nextCode || nextInputValue);
    this.setDiscountInputErrorState(false);
    this.updateDiscountActionState(nextCode, {
      inputValue: nextCode || nextInputValue,
    });

    if (detail.open === true || nextCode) {
      this.setDiscountOpen(true);
    }

    if (detail.refreshPage) {
      this.runtime.pendingDiscountSuccessEffect = Boolean(detail.success);

      try {
        await this.refreshSection(
          detail.announcement || (nextCode ? 'Discount code applied.' : 'Discount code removed.')
        );
      } catch (error) {}

      return;
    }

    if (detail.success) {
      this.triggerDiscountSuccessEffect();
    }
  }

  updateDiscountActionState(discountCode, options = {}) {
    const normalizedDiscountCode = this.normalizeDiscountCodeValue(discountCode);
    this.updateAppliedDiscountCodeDisplays(normalizedDiscountCode);
    if (!this.discountApply || !this.discountInput) return;

    const shouldSyncInput = options.syncInput !== false;
    let currentInputValue = this.normalizeDiscountCodeValue(this.discountInput.value);
    const requestedInputValue =
      options.inputValue !== undefined ? this.normalizeDiscountCodeValue(options.inputValue) : '';
    const effectiveInputValue = normalizedDiscountCode || requestedInputValue || this.getStoredDiscountInputValue();

    if (shouldSyncInput) {
      this.discountInput.value = effectiveInputValue;
      currentInputValue = effectiveInputValue;
    } else if (requestedInputValue) {
      currentInputValue = requestedInputValue;
    }

    const isRemoveState = Boolean(
      normalizedDiscountCode && (!currentInputValue || currentInputValue === normalizedDiscountCode)
    );
    const nextActionLabel = isRemoveState ? this.discountRemoveText : this.discountApplyText;

    if (this.discountApplyLabel) {
      this.discountApplyLabel.textContent = nextActionLabel;
    }

    this.discountApply.setAttribute('aria-label', nextActionLabel);
    this.discountApply.setAttribute('data-discount-action', isRemoveState ? 'remove' : 'apply');
  }

  updateAppliedDiscountCodeDisplays(discountCode) {
    const normalizedDiscountCode = this.normalizeDiscountCodeValue(discountCode);
    if (!Array.isArray(this.appliedDiscountCodeDisplays) || this.appliedDiscountCodeDisplays.length === 0) return;

    this.appliedDiscountCodeDisplays.forEach((element) => {
      if (!element) return;

      if (normalizedDiscountCode) {
        element.hidden = false;
        element.textContent = `Code: ${normalizedDiscountCode}`;
      } else {
        element.hidden = true;
        element.textContent = '';
      }
    });
  }

  async fetchCartState() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart state.');
    }

    const cartState = await response.json();
    this.runtime.lastKnownCartState = cartState;
    return cartState;
  }

  getDiscountApplicationLabel(application) {
    if (!application) return '';

    const source = application.discount_application || application;
    return this.normalizeDiscountCodeValue(source.title || source.code || source.description || '');
  }

  getDiscountApplicationAmount(application) {
    if (!application) return 0;

    return Number.parseInt(
      application.total_allocated_amount !== undefined
        ? application.total_allocated_amount
        : application.amount !== undefined
          ? application.amount
          : application.discounted_amount !== undefined
            ? application.discounted_amount
            : 0,
      10
    ) || 0;
  }

  getCartDiscountApplications(cartState) {
    if (!cartState) return [];

    const applications = [];

    if (Array.isArray(cartState.cart_level_discount_applications)) {
      cartState.cart_level_discount_applications.forEach((application) => {
        const label = this.getDiscountApplicationLabel(application);
        applications.push({
          key: this.normalizeDiscountCodeKey(label),
          label,
          amount: this.getDiscountApplicationAmount(application),
        });
      });
    }

    if (Array.isArray(cartState.items)) {
      cartState.items.forEach((item) => {
        let allocations = [];

        if (Array.isArray(item.line_level_discount_allocations)) {
          allocations = allocations.concat(item.line_level_discount_allocations);
        }

        if (Array.isArray(item.discount_allocations)) {
          allocations = allocations.concat(item.discount_allocations);
        }

        allocations.forEach((allocation) => {
          const label = this.getDiscountApplicationLabel(allocation);
          applications.push({
            key: this.normalizeDiscountCodeKey(label),
            label,
            amount: this.getDiscountApplicationAmount(allocation),
          });
        });
      });
    }

    return applications;
  }

  getCartDiscountApplicationsSignature(cartState) {
    return this.getCartDiscountApplications(cartState)
      .map((application) => [application.key || application.label, application.amount].join(':'))
      .sort()
      .join('|');
  }

  getCartDiscountApplicationsTotal(cartState) {
    return this.getCartDiscountApplications(cartState).reduce((total, application) => {
      return total + Math.max(0, Number.parseInt(application.amount, 10) || 0);
    }, 0);
  }

  getCartLevelDiscountTotal(cartState) {
    if (!cartState) return 0;

    const itemsSubtotalPrice = Number.parseInt(cartState.items_subtotal_price, 10) || 0;
    const totalPrice = Number.parseInt(cartState.total_price, 10) || 0;
    return itemsSubtotalPrice > totalPrice ? Math.max(0, itemsSubtotalPrice - totalPrice) : 0;
  }

  didDiscountCodeApply(beforeState, afterState, discountCode) {
    const normalizedDiscountCode = this.normalizeDiscountCodeKey(discountCode);
    if (!normalizedDiscountCode || !afterState) return false;

    const hasBeforeState = Boolean(beforeState);
    const afterApplications = this.getCartDiscountApplications(afterState);
    const beforeApplicationsSignature = this.getCartDiscountApplicationsSignature(beforeState);
    const afterApplicationsSignature = this.getCartDiscountApplicationsSignature(afterState);
    const beforeApplicationsTotal = this.getCartDiscountApplicationsTotal(beforeState);
    const afterApplicationsTotal = this.getCartDiscountApplicationsTotal(afterState);

    if (
      afterApplications.some((application) => {
        return (
          application.key
          && (application.key.indexOf(normalizedDiscountCode) !== -1
            || normalizedDiscountCode.indexOf(application.key) !== -1)
        );
      })
    ) {
      return true;
    }

    if (!hasBeforeState) return false;
    if (afterApplicationsTotal > beforeApplicationsTotal) return true;
    if (this.getCartLevelDiscountTotal(afterState) > this.getCartLevelDiscountTotal(beforeState)) return true;

    return afterApplications.length > 0 && afterApplicationsSignature !== beforeApplicationsSignature;
  }

  syncStoredDiscountCodeWithCartState(cartState) {
    const storedDiscountCode = this.normalizeDiscountCodeValue(this.getStoredDiscountCode());
    if (!storedDiscountCode) return '';

    const normalizedStoredDiscountCode = this.normalizeDiscountCodeKey(storedDiscountCode);
    const hasMatchingDiscountApplication = this.getCartDiscountApplications(cartState).some((application) => {
      if (!application || !application.key) return false;

      return (
        application.key.indexOf(normalizedStoredDiscountCode) !== -1
        || normalizedStoredDiscountCode.indexOf(application.key) !== -1
      );
    });

    if (!hasMatchingDiscountApplication) {
      this.setStoredDiscountCode('');
      return '';
    }

    return storedDiscountCode;
  }

  async parseCartStateFromResponse(response) {
    if (!response.ok) {
      throw new Error('Cart state response failed.');
    }

    try {
      const cartState = await response.json();
      this.runtime.lastKnownCartState = cartState;
      return cartState;
    } catch (error) {
      return this.fetchCartState();
    }
  }

  async saveNote(value) {
    if (!this.noteInput) return Promise.resolve();

    const normalizedValue = String(value !== undefined ? value : this.noteInput.value || '');
    this.savedNoteValue = normalizedValue;

    this.noteSavePromise = fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        note: normalizedValue,
      }),
    }).catch(() => {});

    return this.noteSavePromise;
  }

  flushNote() {
    if (!this.noteInput) return Promise.resolve();

    const currentValue = String(this.noteInput.value || '');

    if (this.noteTimer) {
      window.clearTimeout(this.noteTimer);
      this.noteTimer = null;
    }

    if (currentValue === this.savedNoteValue) {
      return this.noteSavePromise || Promise.resolve();
    }

    return this.saveNote(currentValue);
  }

  async updateLine(line, quantity, announcement) {
    if (this.isBusy) return;

    this.setBusy(true);

    try {
      await this.flushNote();

      const response = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line,
          quantity,
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to update cart.');
      }

      const cartState = await response.json();
      this.runtime.lastKnownCartState = cartState;
      this.syncStoredDiscountCodeWithCartState(cartState);

      await this.refreshSection(announcement);
    } catch (error) {
      this.announce('Something went wrong while updating the cart.');
      this.setBusy(false);
    }
  }

  async handleDiscountAction() {
    const discountCode = this.discountInput ? this.normalizeDiscountCodeValue(this.discountInput.value) : '';
    const storedCode = this.getStoredDiscountCode();

    if (storedCode && (!discountCode || discountCode === storedCode)) {
      await this.removeDiscountCode();
      return;
    }

    await this.applyDiscountCode(discountCode);
  }

  async applyDiscountCode(discountCode) {
    const normalizedDiscountCode = this.normalizeDiscountCodeValue(discountCode);
    const previouslyStoredDiscount = this.getStoredDiscountCode();

    if (!normalizedDiscountCode) {
      this.setStoredDiscountCode('');
      this.setStoredDiscountInputValue('');
      this.updateDiscountActionState('', { inputValue: '' });
      this.announce('Enter a discount code first.');
      return;
    }

    this.setDiscountInputErrorState(false);
    this.setDiscountBusyState(true);

    try {
      await this.flushNote();

      const beforeState = this.runtime.lastKnownCartState || (await this.fetchCartState().catch(() => null));
      const response = await fetch(
        `/discount/${encodeURIComponent(normalizedDiscountCode)}?redirect=${encodeURIComponent(`${routes.cart_url}.js`)}`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      const nextCartState = await this.parseCartStateFromResponse(response);

      if (!this.didDiscountCodeApply(beforeState, nextCartState, normalizedDiscountCode)) {
        this.setStoredDiscountInputValue(normalizedDiscountCode);
        this.setDiscountInputErrorState(true);
        this.updateDiscountActionState(previouslyStoredDiscount, {
          syncInput: false,
          inputValue: normalizedDiscountCode,
        });
        this.setDiscountBusyState(false);
        return;
      }

      this.setStoredDiscountCode(normalizedDiscountCode);
      this.setStoredDiscountInputValue(normalizedDiscountCode);
      this.setDiscountOpen(true);
      this.setDiscountInputErrorState(false);
      this.runtime.pendingDiscountSuccessEffect = true;
      this.dispatchDiscountSyncEvent({
        source: 'premium-cart',
        code: normalizedDiscountCode,
        inputValue: normalizedDiscountCode,
        open: true,
        refreshDrawer: true,
        success: true,
        announcement: 'Discount code applied.',
      });

      await this.refreshSection('Discount code applied.');
    } catch (error) {
      this.announce('Discount code could not be applied.');
      this.setDiscountBusyState(false);
    }
  }

  async removeDiscountCode() {
    const discountCodeToKeep = this.normalizeDiscountCodeValue(
      (this.discountInput && this.discountInput.value) || this.getStoredDiscountCode() || this.getStoredDiscountInputValue()
    );

    this.setDiscountInputErrorState(false);
    this.setDiscountBusyState(true);

    try {
      await this.flushNote();

      this.setStoredDiscountCode('');
      this.setStoredDiscountInputValue(discountCodeToKeep);

      await fetch('/checkout?discount=', {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      }).catch(() => {});

      const cartState = await this.fetchCartState().catch(() => null);
      if (cartState) {
        this.runtime.lastKnownCartState = cartState;
      }

      this.dispatchDiscountSyncEvent({
        source: 'premium-cart',
        code: '',
        inputValue: discountCodeToKeep,
        open: true,
        refreshDrawer: true,
        announcement: 'Discount code removed.',
      });

      await this.refreshSection('Discount code removed.');
    } catch (error) {
      this.announce('Discount code could not be removed.');
      this.setDiscountBusyState(false);
    }
  }

  async proceedToCheckout() {
    try {
      await this.flushNote();
    } catch (error) {}

    const storedDiscountCode = this.getStoredDiscountCode();
    const checkoutUrl = storedDiscountCode
      ? `/checkout?discount=${encodeURIComponent(storedDiscountCode)}`
      : '/checkout';

    window.location.assign(checkoutUrl);
  }

  async refreshSection(announcement) {
    const response = await fetch(`${routes.cart_url}?section_id=${encodeURIComponent(this.sectionId)}`);

    if (!response.ok) {
      throw new Error('Unable to refresh cart.');
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const updatedSection = documentFragment.querySelector('premium-cart-section');

    if (!updatedSection) {
      throw new Error('Updated cart section was not found.');
    }

    this.replaceWith(updatedSection);

    if (announcement) {
      window.requestAnimationFrame(() => {
        const liveRegion = updatedSection.querySelector('[data-cart-status]');
        if (liveRegion) {
          liveRegion.textContent = announcement;
        }
      });
    }
  }

  announce(message) {
    if (!this.statusNode) return;
    this.statusNode.textContent = message;
  }

  setBusy(state) {
    this.isBusy = state;
    this.dataset.loading = state ? 'true' : 'false';
    this.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  stopDiscountSuccessBurstAnimation() {
    if (this.activeDiscountSuccessBurstFrame) {
      window.cancelAnimationFrame(this.activeDiscountSuccessBurstFrame);
      this.activeDiscountSuccessBurstFrame = 0;
    }
  }

  runDiscountSuccessBurstAnimation() {
    if (!this.successBurst || !this.successBurstCanvas || typeof this.successBurstCanvas.getContext !== 'function') {
      return;
    }

    const context = this.successBurstCanvas.getContext('2d', { alpha: true });
    if (!context) return;

    const viewportWidth = Math.max(window.innerWidth || document.documentElement.clientWidth || 0, 1);
    const viewportHeight = Math.max(window.innerHeight || document.documentElement.clientHeight || 0, 1);
    const viewportCenterX = viewportWidth / 2;
    const viewportCenterY = viewportHeight / 2;
    const viewportDiagonal = Math.sqrt((viewportWidth * viewportWidth) + (viewportHeight * viewportHeight));
    const devicePixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
    const particleCount = Math.min(360, Math.max(240, Math.round((viewportWidth * viewportHeight) / 4200)));
    const colors = ['#5A9CB5', '#FFBC42', '#FF5D8F', '#6C63FF', '#48D3AA', '#FFFFFF', '#FF7A59'];
    const particles = [];
    const animationDuration = 1100;
    const gridColumns = Math.max(10, Math.round(Math.sqrt(particleCount * (viewportWidth / viewportHeight))));
    const gridRows = Math.max(10, Math.ceil(particleCount / gridColumns));
    const cellWidth = viewportWidth / gridColumns;
    const cellHeight = viewportHeight / gridRows;

    this.stopDiscountSuccessBurstAnimation();

    this.successBurstCanvas.width = Math.round(viewportWidth * devicePixelRatio);
    this.successBurstCanvas.height = Math.round(viewportHeight * devicePixelRatio);
    this.successBurstCanvas.style.width = `${viewportWidth}px`;
    this.successBurstCanvas.style.height = `${viewportHeight}px`;
    context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    context.clearRect(0, 0, viewportWidth, viewportHeight);

    for (let index = 0; index < particleCount; index += 1) {
      const gridColumn = index % gridColumns;
      const gridRow = Math.floor(index / gridColumns);
      const targetX = ((gridColumn + 0.5) * cellWidth) + ((Math.random() - 0.5) * cellWidth * 0.75);
      const targetY = ((gridRow + 0.5) * cellHeight) + ((Math.random() - 0.5) * cellHeight * 0.75);
      const startAngle = Math.random() * Math.PI * 2;
      const startRadiusX = viewportWidth * 0.06;
      const startRadiusY = viewportHeight * 0.06;
      const startX = viewportCenterX + (Math.cos(startAngle) * startRadiusX * Math.random());
      const startY = viewportCenterY + (Math.sin(startAngle) * startRadiusY * Math.random());
      const distanceRatio = Math.min(
        1,
        Math.sqrt(((targetX - viewportCenterX) ** 2) + ((targetY - viewportCenterY) ** 2)) / Math.max(viewportDiagonal * 0.5, 1)
      );

      particles.push({
        x: startX,
        y: startY,
        dx: targetX - startX,
        dy: targetY - startY,
        driftX: (Math.random() - 0.5) * 14,
        driftY: (Math.random() - 0.5) * 14,
        radius: 1.6 + (Math.random() * 3.8),
        alpha: 0.68 + (Math.random() * 0.28),
        twinkle: 0.6 + (Math.random() * 0.4),
        distanceRatio,
        color: colors[index % colors.length],
      });
    }

    this.successBurst.classList.add('is-active');

    let animationStartTime = 0;
    const renderFrame = (timestamp) => {
      if (!animationStartTime) {
        animationStartTime = timestamp;
      }

      const progress = Math.min((timestamp - animationStartTime) / animationDuration, 1);
      const easedProgress = 1 - ((1 - progress) ** 3);
      const fadeProgress = Math.sin(Math.min(progress, 1) * Math.PI);

      context.clearRect(0, 0, viewportWidth, viewportHeight);

      particles.forEach((particle) => {
        const driftProgress = 1 - ((1 - progress) ** 2);
        const nextX = particle.x + (particle.dx * easedProgress) + (particle.driftX * driftProgress);
        const nextY = particle.y + (particle.dy * easedProgress) + (particle.driftY * driftProgress);
        const nextAlpha = particle.alpha * fadeProgress * (1 - (progress * 0.08)) * particle.twinkle;
        const nextRadius = particle.radius * (1 - (progress * (0.08 + (particle.distanceRatio * 0.06))));

        if (nextAlpha <= 0.01 || nextRadius <= 0.2) return;

        context.globalAlpha = nextAlpha;
        context.fillStyle = particle.color;
        context.beginPath();
        context.arc(nextX, nextY, nextRadius, 0, Math.PI * 2);
        context.fill();
      });

      context.globalAlpha = 1;

      if (progress < 1) {
        this.activeDiscountSuccessBurstFrame = window.requestAnimationFrame(renderFrame);
        return;
      }

      context.clearRect(0, 0, viewportWidth, viewportHeight);
      this.successBurst.classList.remove('is-active');
      this.activeDiscountSuccessBurstFrame = 0;
    };

    this.activeDiscountSuccessBurstFrame = window.requestAnimationFrame(renderFrame);
  }

  triggerDiscountSuccessEffect() {
    this.runDiscountSuccessBurstAnimation();
  }
}

if (!customElements.get('premium-cart-section')) {
  customElements.define('premium-cart-section', PremiumCartSection);
}
